import React from "react";
import * as Icon from "react-feather";
import clsx from "clsx";
import WComponent from "../../../../../../common/WComponent";
import { TAB_LABELS } from "../../../shared/helpers/constants";
import "../../../afs-walkthrough.css";

class AIReviewSection extends WComponent {
	state = {
		activeView: "internal",
		selectedNotes: {},
		copied: false,
		findingComments: {},
		recommendationComments: {},
	};

	render() {
	let { aiReview, aiReviewLoading, onGenerate, recommendationAcknowledged, onRecommendationAcknowledgedChange, onNavigateToTab } = this.props;
	let { activeView, selectedNotes, copied, findingComments, recommendationComments } = this.state;

	let setRecommendationAcknowledged = onRecommendationAcknowledgedChange;

	// Initialize all notes as selected when AI review first loads
	let initializeSelections = () => {
		if (!aiReview || !aiReview.clientSummary) return;

		let selections = {};
		selections.overview = true;

		let highlights = aiReview.clientSummary.highlights || [];
		for (let highlightIndex = 0; highlightIndex < highlights.length; highlightIndex++) {
			selections["highlight_" + highlightIndex] = true;
		}

		let areas = aiReview.clientSummary.areasOfNote || [];
		for (let areaIndex = 0; areaIndex < areas.length; areaIndex++) {
			selections["area_" + areaIndex] = true;
		}

		selections.lookingForward = true;
		this.update({ selectedNotes: selections });
	};

	// Initialize selections when review data arrives
	if (aiReview && aiReview.clientSummary && Object.keys(selectedNotes).length === 0) {
		initializeSelections();
	}

	let toggleNote = (noteId) => {
		this.setState(function (previous) {
			let updated = { ...previous.selectedNotes };
			updated[noteId] = !updated[noteId];
			return { selectedNotes: updated };
		});
	};

	let buildClientReportText = function () {
		if (!aiReview || !aiReview.clientSummary) return "";

		let lines = [];

		if (selectedNotes.overview) {
			lines.push("FINANCIAL OVERVIEW");
			lines.push(aiReview.clientSummary.overview);
			lines.push("");
		}

		let selectedHighlights = [];
		let highlights = aiReview.clientSummary.highlights || [];
		for (let highlightIndex = 0; highlightIndex < highlights.length; highlightIndex++) {
			if (selectedNotes["highlight_" + highlightIndex]) {
				selectedHighlights.push(highlights[highlightIndex]);
			}
		}

		if (selectedHighlights.length > 0) {
			lines.push("KEY HIGHLIGHTS");
			for (let highlight of selectedHighlights) {
				lines.push("• " + highlight);
			}
			lines.push("");
		}

		let selectedAreas = [];
		let areas = aiReview.clientSummary.areasOfNote || [];
		for (let areaIndex = 0; areaIndex < areas.length; areaIndex++) {
			if (selectedNotes["area_" + areaIndex]) {
				selectedAreas.push(areas[areaIndex]);
			}
		}

		if (selectedAreas.length > 0) {
			lines.push("AREAS OF NOTE");
			for (let area of selectedAreas) {
				lines.push("• " + area);
			}
			lines.push("");
		}

		if (selectedNotes.lookingForward) {
			lines.push("LOOKING FORWARD");
			lines.push(aiReview.clientSummary.lookingForward);
		}

		return lines.join("\n");
	};

	let copyToClipboard = () => {
		let reportText = buildClientReportText();
		navigator.clipboard.writeText(reportText);
		this.update({ copied: true });
		setTimeout(() => { this.update({ copied: false }); }, 2000);
	};

	let NoteToggle = function ({ noteId, children }) {
		let isSelected = selectedNotes[noteId] || false;

		return (
			<div
				className={clsx("afs-review__item", isSelected ? "afs-review__item--manual-checked" : "afs-review__item--manual")}
				onClick={function () { toggleNote(noteId); }}
			>
				{isSelected
					? <Icon.CheckSquare size={16} color="#2e7d32" className="afs-review__icon" />
					: <Icon.Square size={16} color="#ccc" className="afs-review__icon" />
				}
				<div className="afs-review__content">
					{children}
				</div>
			</div>
		);
	};

	let inferLinkedTab = function (text) {
		let lower = (text || "").toLowerCase();
		let bsKeywords = ["cash", "assets", "liabilities", "equity", "receivable", "payable", "investment", "loan", "prepaid", "fixed asset", "shareholder", "retained", "balance sheet", "hst payable", "income tax"];
		let isKeywords = ["revenue", "sales", "expense", "cost of", "gross profit", "net income", "income from operations", "cogs", "operating"];
		for (let keyword of isKeywords) {
			if (lower.includes(keyword)) return "is";
		}
		for (let keyword of bsKeywords) {
			if (lower.includes(keyword)) return "bs";
		}
		return "tb";
	};

	let FindingLink = function ({ linkedTab, text }) {
		if (!onNavigateToTab) return null;
		let tab = linkedTab || inferLinkedTab(text);
		let label = TAB_LABELS[tab] || tab;

		return (
			<button
				className="afs-review__finding-link"
				onClick={function (event) { event.stopPropagation(); onNavigateToTab(tab); }}
			>
				<Icon.ArrowRight size={12} />
				View in {label}
			</button>
		);
	};

	return (
		<div>
			<div className="afs-review__ai-header">
				<button className="afs-review__generate-btn" onClick={onGenerate} disabled={aiReviewLoading}>
					<Icon.Cpu size={14} />
					{aiReviewLoading ? "Analyzing…" : aiReview ? "Regenerate" : "Generate Review"}
				</button>
			</div>

			{!aiReview && !aiReviewLoading && (
				<p className="afs-review__detail">Click "Generate Review" to run an AI analysis of the financial information. The review includes an internal working paper memo and a client-facing summary.</p>
			)}

			{aiReviewLoading && (
				<div className="afs-review__ai-card">
					<p className="afs-review__ai-text">Analyzing financial information…</p>
				</div>
			)}

			{aiReview && !aiReviewLoading && (
				<>
					<div className="afs-review__ai-tabs">
						<button className={clsx("afs-review__ai-tab", activeView === "internal" && "afs-review__ai-tab--active")} onClick={() => { this.update({ activeView: "internal" }); }}>Internal Review</button>
						<button className={clsx("afs-review__ai-tab", activeView === "client" && "afs-review__ai-tab--active")} onClick={() => { this.update({ activeView: "client" }); }}>Client Report</button>
					</div>

					{activeView === "internal" && (
						<>
							<div className="afs-review__ai-card">
								<div className="afs-review__ai-card-title">Overall Assessment</div>
								<div className="afs-review__ai-finding">
									<div className="afs-review__ai-finding-body">
										<p className="afs-review__ai-text">{aiReview.overallAssessment}</p>
									</div>
									<textarea
										className="afs-review__finding-comment"
										placeholder="Add note..."
										value={findingComments["overall"] || ""}
										onChange={(e) => { this.setState(function (prev) { return { findingComments: { ...prev.findingComments, overall: e.target.value } }; }); }}
									></textarea>
								</div>
							</div>

							{aiReview.financialIndicators && aiReview.financialIndicators.length > 0 && (
								<div className="afs-review__ai-card">
									<div className="afs-review__ai-card-title">Financial Indicators</div>
									{aiReview.financialIndicators.map(function (item, itemIndex) {
										return (
											<div key={itemIndex} className="afs-review__ai-finding">
												<div className="afs-review__ai-finding-body">
													<strong>{item.indicator}</strong>: {item.value}
													<br />
													<span className="afs-review__detail">{item.assessment}</span>
													<FindingLink linkedTab={item.linkedTab} linkedSections={item.linkedSections} />
												</div>
												<textarea
													className="afs-review__finding-comment"
													placeholder="Add note..."
													value={findingComments["indicator_" + itemIndex] || ""}
													onChange={(e) => { let key = "indicator_" + itemIndex; let val = e.target.value; this.setState(function (prev) { return { findingComments: { ...prev.findingComments, [key]: val } }; }); }}
												></textarea>
											</div>
										);
									})}
								</div>
							)}

							{aiReview.yearOverYearFindings && aiReview.yearOverYearFindings.length > 0 && (
								<div className="afs-review__ai-card">
									<div className="afs-review__ai-card-title">Year-over-Year Analysis</div>
									{aiReview.yearOverYearFindings.map(function (finding, findingIndex) {
										return (
											<div key={findingIndex} className="afs-review__ai-finding">
												<span className={clsx("afs-review__ai-badge", "afs-review__ai-badge--" + finding.significance)}>{finding.significance}</span>
												<div className="afs-review__ai-finding-body">
													<strong>{finding.item}</strong>
													<br />
													<span className="afs-review__detail">{finding.finding}</span>
													<FindingLink linkedTab={finding.linkedTab} linkedSections={finding.linkedSections} />
												</div>
												<textarea
													className="afs-review__finding-comment"
													placeholder="Add note..."
													value={findingComments["yoy_" + findingIndex] || ""}
													onChange={(e) => { let key = "yoy_" + findingIndex; let val = e.target.value; this.setState(function (prev) { return { findingComments: { ...prev.findingComments, [key]: val } }; }); }}
												></textarea>
											</div>
										);
									})}
								</div>
							)}

							{aiReview.anomaliesAndConcerns && aiReview.anomaliesAndConcerns.length > 0 && (
								<div className="afs-review__ai-card">
									<div className="afs-review__ai-card-title">Anomalies & Concerns</div>
									{aiReview.anomaliesAndConcerns.map(function (concern, concernIndex) {
										return (
											<div key={concernIndex} className="afs-review__ai-finding">
												<span className={clsx("afs-review__ai-badge", "afs-review__ai-badge--" + concern.severity)}>{concern.severity}</span>
												<div className="afs-review__ai-finding-body">
													<strong>{concern.concern}</strong>
													<br />
													<span className="afs-review__detail">{concern.recommendation}</span>
													<FindingLink linkedTab={concern.linkedTab} linkedSections={concern.linkedSections} />
												</div>
												<textarea
													className="afs-review__finding-comment"
													placeholder="Add note..."
													value={findingComments["concern_" + concernIndex] || ""}
													onChange={(e) => { let key = "concern_" + concernIndex; let val = e.target.value; this.setState(function (prev) { return { findingComments: { ...prev.findingComments, [key]: val } }; }); }}
												></textarea>
											</div>
										);
									})}
								</div>
							)}

							{aiReview.recommendations && aiReview.recommendations.length > 0 && (
								<div className="afs-review__ai-card">
									<div className="afs-review__ai-card-title">
										Recommendations
										{aiReview.recommendations.length > 0 && (
											<span className="afs-review__rec-counter">
												{Object.values(recommendationAcknowledged).filter(Boolean).length}/{aiReview.recommendations.length} addressed
											</span>
										)}
									</div>
									{aiReview.recommendations.map(function (recommendation, recIndex) {
										let isAcknowledged = recommendationAcknowledged[recIndex] || false;
										let comment = recommendationComments[recIndex] || "";

										return (
											<div key={recIndex} className="afs-review__ai-finding">
												<div
													className="afs-review__ai-finding-body afs-review__ai-finding-body--clickable"
													onClick={function () {
														setRecommendationAcknowledged(function (previous) {
															let updated = { ...previous };
															updated[recIndex] = !updated[recIndex];
															return updated;
														});
													}}
												>
													<div className="afs-review__ai-finding-check-row">
														{isAcknowledged
															? <Icon.CheckSquare size={16} color="#2e7d32" className="afs-review__ai-check-icon" />
															: <Icon.Square size={16} color="#ccc" className="afs-review__ai-check-icon" />
														}
														<span className={clsx("afs-review__detail", isAcknowledged && "afs-review__detail--pass")}>{recommendation}</span>
													</div>
												</div>
												<textarea
													className="afs-review__finding-comment"
													placeholder="Add note..."
													value={comment}
													onClick={function (event) { event.stopPropagation(); }}
													onChange={(event) => {
														let newValue = event.target.value;
														this.setState(function (previous) {
															let updated = { ...previous.recommendationComments };
															updated[recIndex] = newValue;
															return { recommendationComments: updated };
														});
													}}
												></textarea>
											</div>
										);
									})}
								</div>
							)}
						</>
					)}

					{activeView === "client" && aiReview.clientSummary && (
						<>
							<p className="afs-review__detail afs-review__detail--spaced">Select which findings to include in the client report. Uncheck items you don't want to share.</p>

							{/* Overview */}
							<div className="afs-review__section-title">Financial Overview</div>
							<NoteToggle noteId="overview">
								<p className="afs-review__ai-text">{aiReview.clientSummary.overview}</p>
							</NoteToggle>

							{/* Highlights */}
							{aiReview.clientSummary.highlights && aiReview.clientSummary.highlights.length > 0 && (
								<>
									<div className="afs-review__section-title afs-review__section-title--mt">Key Highlights</div>
									{aiReview.clientSummary.highlights.map(function (highlight, highlightIndex) {
										return (
											<NoteToggle key={highlightIndex} noteId={"highlight_" + highlightIndex}>
												<p className="afs-review__ai-text">{highlight}</p>
											</NoteToggle>
										);
									})}
								</>
							)}

							{/* Areas of Note */}
							{aiReview.clientSummary.areasOfNote && aiReview.clientSummary.areasOfNote.length > 0 && (
								<>
									<div className="afs-review__section-title afs-review__section-title--mt">Areas of Note</div>
									{aiReview.clientSummary.areasOfNote.map(function (area, areaIndex) {
										return (
											<NoteToggle key={areaIndex} noteId={"area_" + areaIndex}>
												<p className="afs-review__ai-text">{area}</p>
											</NoteToggle>
										);
									})}
								</>
							)}

							{/* Looking Forward */}
							<div className="afs-review__section-title afs-review__section-title--mt">Looking Forward</div>
							<NoteToggle noteId="lookingForward">
								<p className="afs-review__ai-text">{aiReview.clientSummary.lookingForward}</p>
							</NoteToggle>

							{/* Copy Button */}
							<div className="afs-review__ai-actions">
								<button className="afs-review__generate-btn" onClick={copyToClipboard}>
									<Icon.Copy size={14} />
									{copied ? "Copied!" : "Copy Report to Clipboard"}
								</button>
							</div>
						</>
					)}
				</>
			)}
		</div>
	);
	}
}

export default AIReviewSection;
